import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const empty = { title: "", slug: "", excerpt: "", content: "", featured_image: "", category: "Recipes", published: true };

const slugify = (s) => s.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [comments, setComments] = useState([]);

  const load = () => api.get("/blog", { params: { published_only: false } }).then(r => setPosts(r.data));
  const loadComments = () => api.get("/admin/comments").then(r => setComments(r.data));

  useEffect(() => { load(); loadComments(); }, []);

  const startEdit = (p) => { setEditing(p); setForm({ ...p }); };
  const startNew = () => { setEditing("new"); setForm(empty); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, slug: form.slug || slugify(form.title) };
      if (editing === "new") await api.post("/blog", body);
      else await api.put(`/blog/${editing.id}`, body);
      toast.success("Saved");
      close(); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/blog/${id}`); load();
  };

  const approve = async (id) => { await api.post(`/admin/comments/${id}/approve`); loadComments(); };
  const delComment = async (id) => { await api.delete(`/admin/comments/${id}`); loadComments(); };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-3xl text-[color:var(--color-primary)]">Blog Manager</h1>
        <button onClick={startNew} className="btn-accent" data-testid="add-blog-btn"><Plus className="w-4 h-4"/> New Post</button>
      </div>

      <div className="bg-white border border-[color:var(--color-border)] rounded overflow-hidden mb-10">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-widest text-gray-500">
            <tr>
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Category</th>
              <th className="text-left p-3">Published</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.id} className="border-t border-[color:var(--color-border)]">
                <td className="p-3 font-medium">{p.title}</td>
                <td className="p-3">{p.category}</td>
                <td className="p-3">{p.published ? "Yes" : "Draft"}</td>
                <td className="p-3 text-right">
                  <button onClick={() => startEdit(p)} className="p-1.5 hover:bg-gray-100 rounded" data-testid={`edit-blog-${p.id}`}><Pencil className="w-4 h-4"/></button>
                  <button onClick={() => remove(p.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded ml-2" data-testid={`delete-blog-${p.id}`}><Trash2 className="w-4 h-4"/></button>
                </td>
              </tr>
            ))}
            {posts.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-gray-500">No posts yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <h2 className="font-display text-2xl text-[color:var(--color-primary)] mb-4">Comments Moderation</h2>
      <div className="bg-white border border-[color:var(--color-border)] rounded p-5 space-y-3">
        {comments.length === 0 && <div className="text-gray-500 text-sm">No comments yet.</div>}
        {comments.map((c) => (
          <div key={c.id} className="border-b border-[color:var(--color-border)] pb-3 last:border-0" data-testid={`mod-comment-${c.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="text-sm font-medium">{c.name} <span className="text-xs text-gray-500">· {c.email}</span></div>
                <div className="text-sm text-gray-700 mt-1">{c.content}</div>
              </div>
              <div className="flex gap-2">
                {!c.approved && <button onClick={() => approve(c.id)} className="text-xs px-3 py-1 bg-emerald-600 text-white rounded" data-testid={`approve-comment-${c.id}`}>Approve</button>}
                <button onClick={() => delComment(c.id)} className="text-xs px-3 py-1 bg-red-500 text-white rounded">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="blog-form-modal">
          <div className="bg-white rounded-lg p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl">{editing === "new" ? "New Post" : "Edit Post"}</h2>
              <button onClick={close}><X className="w-5 h-5"/></button>
            </div>
            <form onSubmit={save} className="space-y-3">
              <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || slugify(e.target.value) })} className="w-full border border-[color:var(--color-border)] rounded px-3 py-2" data-testid="blog-title"/>
              <input placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="w-full border border-[color:var(--color-border)] rounded px-3 py-2" data-testid="blog-slug"/>
              <input placeholder="Featured image URL" value={form.featured_image} onChange={(e) => setForm({ ...form, featured_image: e.target.value })} className="w-full border border-[color:var(--color-border)] rounded px-3 py-2"/>
              <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-[color:var(--color-border)] rounded px-3 py-2"/>
              <textarea required placeholder="Excerpt" rows="2" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="w-full border border-[color:var(--color-border)] rounded px-3 py-2"/>
              <ReactQuill theme="snow" value={form.content} onChange={(v) => setForm({ ...form, content: v })} />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="blog-published"/>
                Published
              </label>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={close} className="btn-ghost flex-1 justify-center">Cancel</button>
                <button type="submit" className="btn-primary flex-1 justify-center" data-testid="blog-save">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
